/**
 * Legal Research Report Renderer
 *
 * Specialized renderer for LRR (Legal Research Report) references.
 * Handles its own data fetching and rendering without citator support.
 *
 * Follows Principle 4: Conditional fetching (hooks only called when component mounted)
 * Follows Principle 5: Data fetching colocated with renderer
 */

import { useReferenceHtml } from '@/content-viewer/hooks/useReferenceHtml'
import { ViewerDocumentKind } from '@/content-viewer/store/schemas'
import ContentViewerErrorState from '../../common/ContentViewerErrorState'
import HTMLBaseRenderer from '../../base-renderers/HTMLBaseRenderer'
import { HtmlReferenceLoadingSkeleton } from '@/chat-common/components/reference-viewer/caselaw/CaseLawOpinionLoadingSkeleton'

type LegalResearchReportRendererProps = {
  s3_path: string
  highlightSentences?: string[]
}

/**
 * LegalResearchReportRenderer Component
 *
 * Renders LRR references without citator functionality.
 * Fetches HTML content from API and displays as plain HTML.
 *
 * @param props.s3_path - S3 directory path for the LRR document
 * @param props.highlightSentences - Optional sentences to highlight in the HTML
 */
export default function LegalResearchReportRenderer(props: LegalResearchReportRendererProps): JSX.Element {
  const { s3_path, highlightSentences } = props

  // Fetch HTML content from API
  // Hook is only called when this component is mounted (LRR type)
  const { htmlContent, isLoading, isError, retry } = useReferenceHtml(ViewerDocumentKind.LRR, s3_path)

  if (isLoading) {
    return (
      <div className="flex-grow overflow-y-auto h-full w-full">
        <HtmlReferenceLoadingSkeleton />
      </div>
    )
  }

  if (isError || !htmlContent) {
    return <ContentViewerErrorState message="Failed to load legal research report." onRetry={retry} />
  }

  return (
    <div className="flex-grow overflow-y-auto h-full w-full">
      <HTMLBaseRenderer htmlContent={htmlContent} highlightSentences={highlightSentences} />
    </div>
  )
}
